/**
 * `schemas/track.ts` — the track shapes, plus the two raw-byte media routes.
 *
 * Contract source: `spec.md` §6.4 (`TrackSummary`, `TrackDetail`, `/peaks`) and §6.11
 * (`/api/stream/:trackId`, `/api/cover/:trackId`).
 *
 * `TrackSummary` is the single most-reused shape in the system: the catalogue, search
 * hits, favorites, history, queue items, playlist pages, playback resume and the
 * `siblings` array are all built on it. It is declared once, here.
 */
import * as z from 'zod';
import { Base64, CatalogueNo, MediaTokenQuerySchema, NonEmptyText, NullableText, PageSchema, RowId, ShortId, SizeBytes, TrackId, TrackIdParams, Uint32, UnixSeconds, } from './common.js';
/* ────────────────────────────────────────────────────────────────────────────
 * Field-level refinements shared with the admin override endpoint
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * A release year. Four digits, kept deliberately wide: a real tag may carry any year
 * and the indexer must not have to guess which century is plausible. `null` when the
 * value is genuinely unknown — `spec.md` §5.2 keeps `year` nullable for that reason.
 */
export const Year = z.int().min(1000).max(9999);
/** BPM. Only ever set from a real source — `spec.md` §5.2: "Never guessed." */
export const Bpm = z.int().min(1).max(400);
/** `tracks.meta_source` (`spec.md` §5.2 / §11.9). */
export const META_SOURCES = ['tags', 'filename', 'caption', 'mixed', 'manual'];
export const MetaSource = z.enum(META_SOURCES);
/** `tracks.meta_confidence`, 0..100 (`spec.md` §11.9). */
export const MetaConfidence = z.int().min(0).max(100);
/** `tags.kind` — the closed set the DDL comment in `spec.md` §5.2 enumerates. */
export const TAG_KINDS = ['riddim', 'label', 'style', 'year', 'misc'];
export const TagKind = z.enum(TAG_KINDS);
/* ────────────────────────────────────────────────────────────────────────────
 * Plate and cover sub-objects
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * The label side, as the **wire** spells it: `"A" | "B"` (`spec.md` §6.4).
 *
 * `derivePlate()` in `plate.ts` (T07) returns the *rendered* form, `'SIDE A'` /
 * `'SIDE B'`, because that is the string the sleeve prints. Whoever serialises a track
 * must map one to the other; they are not the same enum and this comment exists so the
 * mismatch is read as intentional.
 */
export const PLATE_SIDES = ['A', 'B'];
export const PlateSide = z.enum(PLATE_SIDES);
/**
 * The deterministic sleeve derivation carried on the wire. Only the three fields the
 * client cannot recompute cheaply are sent; `pattern`, `tilt` and `catalogueNo` are
 * either re-derived client-side from `seed` or sent at the top level of the summary.
 */
export const TrackPlateSchema = z.strictObject({
    seed: Uint32,
    pair: z.int().min(0).max(5),
    side: PlateSide,
});
/**
 * The **wire** cover state — `"none" | "blur" | "cached"` (`spec.md` §6.4).
 *
 * Narrower than `tracks.cover_state` in the DDL, which also has `unknown` and
 * `stripped`. The API collapses them: `unknown`/`none` → `none`, `stripped`/`thumb`
 * → `blur`, `cached` → `cached`. See `DbCoverState` below for the storage-side enum.
 */
export const COVER_STATES = ['none', 'blur', 'cached'];
export const CoverState = z.enum(COVER_STATES);
/** `tracks.cover_state` as stored (`spec.md` §5.2). Not a wire value. */
export const DB_COVER_STATES = ['unknown', 'none', 'stripped', 'thumb', 'cached'];
export const DbCoverState = z.enum(DB_COVER_STATES);
/**
 * The cover reference. `blurB64` is the inline `PhotoStrippedSize` JPEG — delivered
 * inside the catalogue JSON precisely so the grid needs zero image requests to paint
 * (`spec.md` §6.11, last paragraph).
 */
export const TrackCoverSchema = z.strictObject({
    state: CoverState,
    blurB64: Base64.nullable(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * TrackSummary — spec.md §6.4
 * ──────────────────────────────────────────────────────────────────────────── */
export const TrackSummarySchema = z.strictObject({
    id: TrackId,
    shortId: ShortId,
    title: NonEmptyText,
    /**
     * `null` when genuinely unknown — never `''`. `NullableText` enforces the min-length,
     * which is what makes `{ performer: '' }` a validation failure (T08 acceptance 4).
     */
    performer: NullableText,
    artistId: RowId.nullable(),
    durationS: z.int().nonnegative(),
    /** TL long as a decimal string: audio files exceed 2^31 and the Bot API warns on it. */
    sizeBytes: SizeBytes,
    postedAt: UnixSeconds,
    year: Year.nullable(),
    bpm: Bpm.nullable(),
    catalogueNo: CatalogueNo,
    plate: TrackPlateSchema,
    cover: TrackCoverSchema,
    hasPeaks: z.boolean(),
    isFavorite: z.boolean(),
    /** `tg_grouped_id`. DISPLAY GROUPING ONLY — an album is never collapsed (§5.2). */
    groupedId: NonEmptyText.nullable(),
});
/** `Page<TrackSummary>` — the return shape of most listing endpoints. */
export const TrackPageSchema = PageSchema(TrackSummarySchema);
/* ────────────────────────────────────────────────────────────────────────────
 * TrackDetail — spec.md §6.4
 * ──────────────────────────────────────────────────────────────────────────── */
export const TrackTagSchema = z.strictObject({
    id: RowId,
    kind: TagKind,
    name: NonEmptyText,
});
export const TrackArtistSchema = z.strictObject({
    id: RowId,
    name: NonEmptyText,
    trackCount: z.int().nonnegative(),
});
/**
 * Where the track lives in the channel. `permalink` is
 * `https://t.me/<username>/<messageId>` when the channel has a username, else `null` —
 * a private channel has no shareable post URL and the spec forbids inventing one.
 */
export const TrackChannelSchema = z.strictObject({
    messageId: z.int().positive(),
    postedAt: UnixSeconds,
    permalink: z.url().nullable(),
});
export const TrackStatsSchema = z.strictObject({
    playCount: z.int().nonnegative(),
    lastPlayedAt: UnixSeconds.nullable(),
});
export const TrackDetailSchema = TrackSummarySchema.extend({
    album: NullableText,
    musicalKey: NullableText,
    fileName: NullableText,
    caption: NullableText,
    mimeType: NonEmptyText,
    metaSource: MetaSource,
    metaConfidence: MetaConfidence,
    /** 512 uint8 peaks, base64. `null` means the UI degrades — it does not fabricate. */
    peaksB64: Base64.nullable(),
    /**
     * A tempo the app MEASURED off the audio — never the same field as {@link Bpm}.
     *
     * `bpm` is a catalogue fact and stays `null` until a real source supplies one
     * (§11.6; `0001_init.sql:96` "Never guessed"). This is the other thing: what an
     * onset analyser heard, written only when the analyser's two independent passes
     * over the same track agreed. Where they did not, this is `null` and the screen
     * prints no tempo at all rather than a number nobody can check.
     *
     * On `TrackDetail` and nowhere else, deliberately: Now Playing is the one screen
     * that has a use for it, and widening every listing query to carry it would cost
     * eight query files for a value no list prints. See `0003_bpm_measured.sql`.
     */
    bpmMeasured: Bpm.nullable(),
    tags: z.array(TrackTagSchema),
    artist: TrackArtistSchema.nullable(),
    channel: TrackChannelSchema,
    /** Same `groupedId`, excluding self, max 12. */
    siblings: z.array(TrackSummarySchema).max(12),
    stats: TrackStatsSchema,
});
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/tracks/:id/peaks — spec.md §6.4
 * ──────────────────────────────────────────────────────────────────────────── */
/** The fixed peaks resolution on the wire. `codec/peaks.ts` (T09) owns the constant. */
export const TrackPeaksResponseSchema = z.strictObject({
    peaks: Base64.nullable(),
    resolution: z.literal(512),
});
/* ────────────────────────────────────────────────────────────────────────────
 * §6.11 — the raw-byte media routes
 *
 * Both return bytes on success and a JSON `Err` on failure, so there is no response
 * schema to declare; what IS declarable is the request surface, and getting the
 * `?t=` shape wrong is one of the cheap 403s §9.4 wants rejected before any I/O.
 * ──────────────────────────────────────────────────────────────────────────── */
/** `GET|HEAD /api/stream/:trackId` path params. */
export const StreamParamsSchema = TrackIdParams;
/** `GET|HEAD /api/stream/:trackId?t=…` querystring. */
export const StreamQuerySchema = MediaTokenQuerySchema;
/** `GET /api/cover/:trackId` path params. */
export const CoverParamsSchema = TrackIdParams;
/** `GET /api/cover/:trackId?t=…` querystring. */
export const CoverQuerySchema = MediaTokenQuerySchema;
//# sourceMappingURL=track.js.map